// src/services/businessAPI.js
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Create axios instance
const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach JWT token to every request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Global response handler
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/auth/login';
    }
    return Promise.reject(error);
  }
);

export const businessAPI = {
  // ===================================================
  // PHASE 1: BUSINESS PROFILE
  // ===================================================
  saveProfile: async (profileData) => {
    try {
      const response = await api.post('/business/profile', profileData);
      return response.data;
    } catch (error) {
      console.error('API: Save profile error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  getProfile: async () => {
    try {
      const response = await api.get('/business/profile');
      return response.data;
    } catch (error) {
      console.error('API: Get profile error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  deleteProfile: async () => {
    try {
      const response = await api.delete('/business/profile');
      return response.data;
    } catch (error) {
      console.error('API: Delete profile error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  markPhase1Complete: async () => {
    try {
      const response = await api.put('/business/profile/complete');
      return response.data;
    } catch (error) {
      console.error('API: Mark phase1 complete error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  getProfileStatus: async () => {
    try {
      const response = await api.get('/business/profile/status');
      return response.data;
    } catch (error) {
      console.error('API: Get profile status error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  uploadLogo: async (file) => {
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const response = await api.post('/business/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      console.error('API: Upload logo error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  uploadStyleExample: async (file) => {
    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await api.post('/business/upload-style-example', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      console.error('API: Upload style example error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // ===================================================
  // PHASE 2: STRATEGY CRUD
  // ===================================================
  getStrategy: async () => {
    try {
      const response = await api.get('/strategy');
      return response.data;
    } catch (error) {
      console.error('API: Get strategy error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  saveStrategy: async (strategyData) => {
    try {
      const response = await api.post('/strategy', strategyData);
      return response.data;
    } catch (error) {
      console.error('API: Save strategy error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  deleteStrategy: async () => {
    try {
      const response = await api.delete('/strategy');
      return response.data;
    } catch (error) {
      console.error('API: Delete strategy error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  getStrategyStatus: async () => {
    try {
      const response = await api.get('/strategy/status');
      return response.data;
    } catch (error) {
      console.error('API: Get strategy status error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  markPhase2Complete: async () => {
    try {
      const response = await api.put('/strategy/complete');
      return response.data;
    } catch (error) {
      console.error('API: Mark phase2 complete error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // ===================================================
  // PHASE 2: AI GENERATION
  // ===================================================
  generatePositioning: async (data) => {
    try {
      const response = await api.post('/strategy/generate-positioning', data);
      return response.data;
    } catch (error) {
      console.error('API: Generate positioning error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  generateMarketingMix: async (data) => {
    try {
      const response = await api.post('/strategy/generate-mix', data);
      return response.data;
    } catch (error) {
      console.error('API: Generate marketing mix error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  generateChannels: async (data) => {
    try {
      const response = await api.post('/strategy/generate-channels', data);
      return response.data;
    } catch (error) {
      console.error('API: Generate channels error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  analyzeBusinessAudit: async (data) => {
    try {
      const response = await api.post('/strategy/analyze-audit', data);
      return response.data;
    } catch (error) {
      console.error('API: Analyze business audit error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Generate Google Review Strategy (Phase 7)
  generateReviewStrategy: async (reviewData) => {
    try {
      const response = await api.post('/strategy/generate-review-strategy', reviewData);
      return response.data;
    } catch (error) {
      console.error('API: Generate review strategy error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  generateProposal: async () => {
    try {
      const response = await api.post('/strategy/generate-proposal');
      return response.data;
    } catch (error) {
      console.error('API: Generate proposal error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  approveStrategy: async () => {
    try {
      const response = await api.post('/strategy/approve-strategy');
      return response.data;
    } catch (error) {
      console.error('API: Approve strategy error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  approveProposal: async () => {
    try {
      const response = await api.post('/strategy/approve-proposal');
      return response.data;
    } catch (error) {
      console.error('API: Approve proposal error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  generateDailyBreakdown: async (data) => {
    try {
      const response = await api.post('/strategy/generate-daily-breakdown', data);
      return response.data;
    } catch (error) {
      console.error('API: Generate daily breakdown error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // ===================================================
  // MEGA STRATEGY (MVP v2 - single-call generation)
  // ===================================================
  generateMegaStrategy: async (data) => {
    try {
      const response = await api.post('/strategy/generate-mega-strategy', data, {
        timeout: 300000 // 5 minutes - AI generation with full Brand DNA takes time
      });
      return response.data;
    } catch (error) {
      console.error('API: Generate mega strategy error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  approveMegaStrategy: async () => {
    try {
      const response = await api.post('/strategy/approve-mega-strategy');
      return response.data;
    } catch (error) {
      console.error('API: Approve mega strategy error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // ===================================================
  // PHASE 2: CONNECTIVITY
  // ===================================================
  connectChannel: async (data) => {
    try {
      const response = await api.post('/strategy/connect-channel', data);
      return response.data;
    } catch (error) {
      console.error('API: Connect channel error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  disconnectChannel: async (data) => {
    try {
      const response = await api.post('/strategy/disconnect-channel', data);
      return response.data;
    } catch (error) {
      console.error('API: Disconnect channel error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  startInstagramAuth: async () => {
    try {
      const response = await api.get('/strategy/auth/instagram');
      return response.data;
    } catch (error) {
      console.error('API: Instagram auth start error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  handleInstagramCallback: async (code) => {
    try {
      const response = await api.post('/strategy/auth/instagram/callback', { code });
      return response.data;
    } catch (error) {
      console.error('API: Instagram callback error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // ===================================================
  // PHASE 3: ROADMAP CRUD
  // ===================================================
  getRoadmap: async () => {
    try {
      const response = await api.get('/roadmap');
      return response.data;
    } catch (error) {
      console.error('API: Get roadmap error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  saveRoadmap: async (roadmapData) => {
    try {
      const response = await api.post('/roadmap', roadmapData);
      return response.data;
    } catch (error) {
      console.error('API: Save roadmap error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  deleteRoadmap: async () => {
    try {
      const response = await api.delete('/roadmap');
      return response.data;
    } catch (error) {
      console.error('API: Delete roadmap error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  getRoadmapStatus: async () => {
    try {
      const response = await api.get('/roadmap/status');
      return response.data;
    } catch (error) {
      console.error('API: Get roadmap status error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  markPhase3Complete: async () => {
    try {
      const response = await api.put('/roadmap/complete');
      return response.data;
    } catch (error) {
      console.error('API: Mark phase3 complete error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // ===================================================
  // PHASE 3: AI CONTENT GENERATION
  // ===================================================
  generateSocialPost: async (contentData) => {
    try {
      const response = await api.post('/roadmap/generate-content', contentData);
      return response.data;
    } catch (error) {
      console.error('API: Generate social post error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  generateContent: async (contentData) => {
    try {
      const response = await api.post('/roadmap/generate-content', contentData);
      return response.data;
    } catch (error) {
      console.error('API: Generate content error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  generateImage: async (imageData) => {
    try {
      const response = await api.post('/roadmap/generate-image', imageData);
      return response.data;
    } catch (error) {
      console.error('API: Generate image error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Generate 3 Image Variations with Brand DNA
  generateImageVariations: async (imageData) => {
    try {
      const response = await api.post('/roadmap/generate-image-variations', imageData, {
        timeout: 300000 // 5 min timeout for 3 images
      });
      return response.data;
    } catch (error) {
      console.error('API: Generate image variations error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Generate Video Hook Options
  generateVideoHooks: async (hookData) => {
    try {
      const response = await api.post('/roadmap/generate-video-hooks', hookData);
      return response.data;
    } catch (error) {
      console.error('API: Generate video hooks error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Generate full video script (scenes, voiceover, B-roll, caption, hashtags)
  generateVideoScript: async (scriptData) => {
    try {
      const response = await api.post('/roadmap/generate-video-script', scriptData);
      return response.data;
    } catch (error) {
      console.error('API: Generate video script error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Generate AI Auto-Edit plan (text overlays, filter, music, captions) for a video
  generateVideoEditPlan: async (editPlanData) => {
    try {
      const response = await api.post('/roadmap/generate-video-edit-plan', editPlanData);
      return response.data;
    } catch (error) {
      console.error('API: Generate video edit plan error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Generate Task Briefing for a calendar day
  generateTaskBriefing: async (briefingData) => {
    try {
      const response = await api.post('/roadmap/generate-task-briefing', briefingData);
      return response.data;
    } catch (error) {
      console.error('API: Generate task briefing error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Generate Image Text (headline, subtext, CTA)
  generateImageText: async (textData) => {
    try {
      const response = await api.post('/roadmap/generate-image-text', textData);
      return response.data;
    } catch (error) {
      console.error('API: Generate image text error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Generate a detailed AI image prompt from post context (prompt engineering)
  generateImagePrompt: async (data) => {
    try {
      const response = await api.post('/roadmap/generate-image-prompt', data);
      return response.data;
    } catch (error) {
      console.error('API: Generate image prompt error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Generate Base Image (clean, text-free, brand-aligned for Design Studio)
  generateBaseImage: async (imageData) => {
    try {
      const response = await api.post('/roadmap/generate-base-image', imageData, {
        timeout: 300000
      });
      return response.data;
    } catch (error) {
      console.error('API: Generate base image error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Generate Blog Post Content
  generateBlogPost: async (blogData) => {
    try {
      const response = await api.post('/roadmap/generate-blog', blogData);
      return response.data;
    } catch (error) {
      console.error('API: Generate blog post error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Generate Email Campaign Content
  generateEmailCampaign: async (emailData) => {
    try {
      const response = await api.post('/roadmap/generate-email', emailData);
      return response.data;
    } catch (error) {
      console.error('API: Generate email campaign error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Generate Ad Campaign Content
  generateAdCampaign: async (adData) => {
    try {
      const response = await api.post('/roadmap/generate-ad-campaign', adData);
      return response.data;
    } catch (error) {
      console.error('API: Generate ad campaign error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Generate SEO Strategy
  generateSEOStrategy: async (seoData) => {
    try {
      const response = await api.post('/roadmap/generate-seo-strategy', seoData);
      return response.data;
    } catch (error) {
      console.error('API: Generate SEO strategy error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Generate Analytics Insights
  generateAnalyticsInsights: async (analyticsData) => {
    try {
      const response = await api.post('/roadmap/generate-analytics-insights', analyticsData);
      return response.data;
    } catch (error) {
      console.error('API: Generate analytics insights error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Generate Weekly Review
  generateWeeklyReview: async (reviewData) => {
    try {
      const response = await api.post('/roadmap/generate-weekly-review', reviewData);
      return response.data;
    } catch (error) {
      console.error('API: Generate weekly review error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // ===================================================
  // PHASE 3: ROADMAP TASKS / DAYS
  // ===================================================
  updateDay: async (dayNumber, dayData) => {
    try {
      const response = await api.put(`/roadmap/day/${dayNumber}`, dayData);
      return response.data;
    } catch (error) {
      console.error('API: Update day error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  updateDayContent: async (dayNumber, dayData) => {
    try {
      const response = await api.put(`/roadmap/day/${dayNumber}/content`, dayData);
      return response.data;
    } catch (error) {
      console.error('API: Update day content error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  toggleTask: async (taskId) => {
    try {
      const response = await api.put(`/roadmap/task/${taskId}`);
      return response.data;
    } catch (error) {
      console.error('API: Toggle task error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

// ===================================================
  // PHASE 4: RESULTS CRUD
  // ===================================================
  getResults: async () => {
    try {
      const response = await api.get('/results');
      return response.data;
    } catch (error) {
      console.error('API: Get results error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  saveResults: async (resultsData) => {
    try {
      const response = await api.post('/results', resultsData);
      return response.data;
    } catch (error) {
      console.error('API: Save results error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  deleteResults: async () => {
    try {
      const response = await api.delete('/results');
      return response.data;
    } catch (error) {
      console.error('API: Delete results error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  getResultsStatus: async () => {
    try {
      const response = await api.get('/results/status');
      return response.data;
    } catch (error) {
      console.error('API: Get results status error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  markPhase4Complete: async () => {
    try {
      const response = await api.put('/results/complete');
      return response.data;
    } catch (error) {
      console.error('API: Mark phase4 complete error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // ===================================================
  // PHASE 4: WEEK METRICS
  // ===================================================
  saveWeekMetrics: async (weekNumber, metricsData) => {
    try {
      const response = await api.post(`/results/week/${weekNumber}`, metricsData);
      return response.data;
    } catch (error) {
      console.error('API: Save week metrics error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  getResultsSummary: async () => {
    try {
      const response = await api.get('/results/summary');
      return response.data;
    } catch (error) {
      console.error('API: Get results summary error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // ===================================================
  // SUBSCRIPTION API
  // ===================================================

  /**
   * Get current subscription and usage data
   * @returns {Promise<Object>} { success, data: { currentTier, status, usage, ... } }
   */
  getSubscription: async () => {
    try {
      const response = await api.get('/subscription');
      return response.data;
    } catch (error) {
      console.error('API: Get subscription error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Create Stripe checkout session for tier upgrade
   * @param {string} tier - 'diy' | 'assisted' | 'managed'
   * @returns {Promise<Object>} { success, data: { sessionId, checkoutUrl } }
   */
  createCheckoutSession: async (tier) => {
    try {
      const response = await api.post('/subscription/checkout', { tier });
      return response.data;
    } catch (error) {
      console.error('API: Create checkout session error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // ===================================================
  // ASSET LIBRARY
  // ===================================================

  /**
   * Upload assets (FormData with 'files' field)
   */
  uploadAssets: async (formData) => {
    try {
      const response = await api.post('/assets/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      console.error('API: Upload assets error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Get all assets with optional filters
   */
  getAssets: async ({ category, search, limit, offset } = {}) => {
    try {
      const params = {};
      if (category && category !== 'all') params.category = category;
      if (search) params.search = search;
      if (limit) params.limit = limit;
      if (offset) params.offset = offset;
      const response = await api.get('/assets', { params });
      return response.data;
    } catch (error) {
      console.error('API: Get assets error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Update asset metadata
   */
  updateAsset: async (assetId, data) => {
    try {
      const response = await api.put(`/assets/${assetId}`, data);
      return response.data;
    } catch (error) {
      console.error('API: Update asset error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Delete an asset
   */
  deleteAsset: async (assetId) => {
    try {
      const response = await api.delete(`/assets/${assetId}`);
      return response.data;
    } catch (error) {
      console.error('API: Delete asset error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Save a data URL or external URL image to the asset library
   */
  saveImageToLibrary: async (data) => {
    try {
      const response = await api.post('/assets/save-image', data);
      return response.data;
    } catch (error) {
      console.error('API: Save image to library error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Mark asset as used (increment usage counter)
   */
  markAssetUsed: async (assetId) => {
    try {
      const response = await api.post(`/assets/${assetId}/use`);
      return response.data;
    } catch (error) {
      console.error('API: Mark asset used error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Remove background from an image using Replicate cjwbw/rembg.
   * imageData: base64 dataURL (data:image/...) or a public https:// URL
   * Returns: { success: true, imageData: "<base64 PNG with transparent bg>" }
   */
  removeBackground: async (imageData) => {
    try {
      const response = await api.post('/assets/remove-bg', { imageData });
      return response.data;
    } catch (error) {
      console.error('API: Remove background error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  }
};

export default businessAPI;